import React, { FC } from "react"
import { Autocomplete, Checkbox, FormControlLabel, FormHelperText, MenuItem, TextField } from "@mui/material"
import { IUiProps } from "../../../../types/uiProps"

export type ISelectOption = string | { value: string; label: string };

interface IProps {
    onChange?: (e:any) => void,
    onBlur?: () => void
    name?: string,
    value?: string | number ,
    placeholder?: React.ReactNode,
    uiProps?: IUiProps,
    type?: string,
    isError?: boolean,
    errorMessage?: string,
    disabled?: boolean,
    isRequired?: boolean;
    readOnly?:boolean;
    id?: string;
    // type="select" only: the dropdown's options, as plain strings or
    // {value, label} pairs.
    options?: ISelectOption[];
    // type="select" only: renders as a searchable MUI Autocomplete instead
    // of a plain dropdown - for option lists long enough (e.g. countries)
    // that scrolling through them beats typing to filter.
    searchable?: boolean;
    // type="checkbox" only: controls the checkbox explicitly, for the
    // rare caller that already tracks a real checked/unchecked state
    // (as opposed to the default uncontrolled checkbox behavior below).
    checked?: boolean;
}

export const Input:FC<IProps> = ({
    name,
    value,
    onChange,
    errorMessage,
    isError,
    onBlur,
    placeholder,
    uiProps,
    type,
    disabled,
    isRequired,
    readOnly,
    id,
    options,
    checked,
    searchable,
}) => {
    if (type === "select" && searchable) {
        // Same {value, label} normalization as the plain select branch
        // below, but rendered as a searchable Autocomplete - long option
        // lists (e.g. every GLEIF country) are painful to scroll through
        // with a plain dropdown.
        const normalizedOptions = (options ?? []).map((option) =>
            typeof option === "string" ? { value: option, label: option } : option
        );
        const selectedOption = normalizedOptions.find((option) => option.value === value) ?? null;

        return (
            <Autocomplete
                options={normalizedOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                value={selectedOption}
                onChange={(_, selected) => {
                    onChange?.({ target: { name, value: selected?.value ?? "" } });
                }}
                onBlur={onBlur}
                disabled={disabled}
                size="small"
                fullWidth
                sx={{ minWidth: 0 }}
                style={uiProps as React.CSSProperties}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        id={id ?? name}
                        name={name}
                        label={placeholder}
                        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                        required={isRequired}
                        error={isError}
                        helperText={isError ? errorMessage : undefined}
                    />
                )}
            />
        );
    }

    if (type === "select") {
        // Renders as a MUI TextField (select variant) rather than a plain
        // native <select> so it shares the exact same box height, border
        // and label placement as a text-mode Input - keeping selects,
        // inputs, and buttons sitting on the same line in a form row.
        return (
            <TextField
                select
                size="small"
                id={id ?? name}
                name={name}
                label={placeholder}
                InputLabelProps={{ shrink: true }}
                value={value ?? ""}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                required={isRequired}
                error={isError}
                helperText={isError ? errorMessage : undefined}
                fullWidth
                // A long option (e.g. a full jurisdiction name) would
                // otherwise force this field - and the CSS grid column it
                // sits in - to grow to fit it, blowing up the row's width
                // on desktop and overflowing the viewport on mobile.
                // Truncating the closed value with an ellipsis keeps the
                // field's own width fixed; minWidth: 0 lets the grid
                // column itself shrink instead of sizing to the content.
                sx={{
                    minWidth: 0,
                    "& .MuiSelect-select": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    },
                }}
                SelectProps={{
                    MenuProps: {
                        PaperProps: { sx: { maxWidth: "min(90vw, 400px)" } },
                    },
                }}
                style={uiProps as React.CSSProperties}
            >
                {(options ?? []).map((option) => {
                    const optionValue = typeof option === "string" ? option : option.value;
                    const optionLabel = typeof option === "string" ? option : option.label;
                    return (
                        <MenuItem key={optionValue} value={optionValue} sx={{ whiteSpace: "normal" }}>
                            {optionLabel}
                        </MenuItem>
                    );
                })}
            </TextField>
        );
    }

    if (type === "checkbox") {
        // Uncontrolled, matching the original native <input type="checkbox">
        // behavior exactly: several callers never pass `value` at all (it
        // wouldn't control a checkbox's checked state anyway), so the
        // checkbox has always relied on the browser's own toggle state,
        // with onChange only mirroring it out to the caller's own state.
        // MUI's default medium checkbox has a 42x42px hit area, much
        // larger than the original native checkbox - callers lay it out
        // next to text using their own flex/margin rules sized for that
        // original, much smaller box. Sizing it down here keeps every
        // checkbox in the app the same compact size instead of leaving
        // that up to each caller.
        const checkboxProps = {
            id: id ?? name,
            onChange,
            disabled,
            required: isRequired,
            size: "small" as const,
            ...(checked !== undefined ? { checked } : {}),
        };
        return (
            // flexShrink: 0 - callers place this next to a long, wrapping
            // text label inside their own flex row. Without it, Firefox's
            // flex algorithm shrinks this box (and the checkbox inside it)
            // to fit, sometimes down to just a few pixels wide, the longer
            // the sibling text is. Chrome doesn't do this, which is why the
            // checkbox only looked broken in Firefox.
            <div style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, ...(uiProps as React.CSSProperties) }}>
                {/* FormControlLabel renders as an HTML <label> - only use it
                when it's actually carrying the label text. Several callers
                render their own surrounding <label> with the checkbox text
                (no placeholder passed here), and nesting a <label> inside a
                <label> is invalid HTML that breaks the layout. */}
                {placeholder ? (
                    <FormControlLabel
                        control={<Checkbox {...checkboxProps} sx={{ padding: "4px", flexShrink: 0 }} />}
                        label={placeholder}
                    />
                ) : (
                    // marginTop: callers top-align this against their own
                    // sibling text (see .label's align-items: flex-start),
                    // but the checkbox's padding box sits a couple pixels
                    // lower than the text's first line - nudge it up to
                    // match.
                    <Checkbox {...checkboxProps} sx={{ padding: "4px", marginRight: "0.5em", marginTop: "-2px", flexShrink: 0 }} />
                )}
                {isError && <FormHelperText error>{errorMessage}</FormHelperText>}
            </div>
        );
    }

    return (
        <TextField
            id={id ?? name}
            name={name}
            type={type || "text"}
            size="small"
            // required={isRequired} below already appends MUI's own
            // asterisk to the label - don't add a second one here.
            label={placeholder}
            // Label always sits above the field rather than floating inside
            // it, matching the original design's permanent-label look.
            InputLabelProps={{ shrink: true }}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            required={isRequired}
            InputProps={{ readOnly }}
            error={isError}
            helperText={isError ? errorMessage : undefined}
            fullWidth
            style={uiProps as React.CSSProperties}
        />
    );
}
